import React, { useState, useEffect, useRef } from "react";
import "../styles/Requisitions.scss";
import { Link, useNavigate } from "react-router-dom";
import { FaList, FaUserPlus, FaEye, FaEdit } from "react-icons/fa";
import Swal from "sweetalert2";
import Select, { components } from "react-select";
import {
  getAllRequisitions,
  getAssignedJobsForRecruiter,
  updateRequisitionStatus,
  assignRecruiters,
  getAllRecruiters,
  getCandidatesByRequisition,
  getRecruiterMappings,
  reconcileAllocations,
} from "../services/api";
import ExcelJS from "exceljs";

const ITEMS_PER_PAGE = 10;

function Requisitions() {
  const navigate = useNavigate();
  const [requisitions, setRequisitions] = useState([]);
  const [recruiterModalOpen, setRecruiterModalOpen] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [selectedRecruiters, setSelectedRecruiters] = useState([]);
  const [recruiterTargets, setRecruiterTargets] = useState({});
  const [originalSelectedRecruiters, setOriginalSelectedRecruiters] = useState([]);
  const [originalRecruiterTargets, setOriginalRecruiterTargets] = useState({});
  const [recruiterOptions, setRecruiterOptions] = useState([]);
  const suppressValidationRef = useRef(false);
  const [, setJoinedCandidatesCount] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [recruiterMenuOpen, setRecruiterMenuOpen] = useState(false);
  const [isLocked,setLocked] = useState(false);

  const CheckboxOption = (props) => (
    <components.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        style={{ marginRight: 8 }}
      />
      <label>{props.label}</label>
    </components.Option>
  );

  // Get role_id and user data from localStorage
  const roleId = localStorage.getItem("role_id");
  const userData = localStorage.getItem("userData");
  const userId = userData ? JSON.parse(userData)?.user?.id : null;


  // For get all job requisitions data
  const fetchRequisitions = React.useCallback(async () => {
    try {
      let res;
      // 📌 Recruiter role (roleId === "4")
if (roleId === "4") {
  if (!userId) {
    console.error("User ID not found for recruiter");
    return;
  }
  res = await getAssignedJobsForRecruiter(userId);
} else {
  res = await getAllRequisitions();
}

if (res?.result_code === 200 && Array.isArray(res.result_info)) {
  if (roleId === "4") {
    const recruiterData = res.result_info.map((r) => ({
  ...r,
  id: r.requisition_id || r.id,
  mapping_id: r.mapping_id,
  requisition_id: r.requisition_id,
  parent_job_id: r.parent_job_id || r.job_id,
  openings: Number(r.openings ?? r.target ?? 1),
  status: r.status,          // child-level status (Open/Hold/Closed)
  recruiter_target: r.target ?? r.recruiter_target ?? 0,  // individual target
}));

    setRequisitions(recruiterData);
    return;
  }

  // KEEP YOUR OLD GROUPING (FOR HIRING MANAGER)
  const grouped = {};

 res.result_info.forEach((r) => {
  const key = r.job_id;

  const isParentRequisition = !r.child_job_id || 
    r.child_job_id?.endsWith('-master') || 
    r.child_job_id?.includes('placeholder');

  if (!grouped[key]) {
    grouped[key] = {
      ...r,
      recruiter_details: [],
      recruiter_target: [],
      child_ids: [],
      child_job_ids: [],
      child_statuses: [],  // track each child's status
      openings: 0,
      submissions_count: 0,
      joined_count: 0,
      parent_status: isParentRequisition ? r.status : null,
      master_status: r.master_status || null,
    };
  } else {
    if (isParentRequisition && !grouped[key].parent_status) {
      grouped[key].parent_status = r.status;
    }
    if (r.master_status && !grouped[key].master_status) {
      grouped[key].master_status = r.master_status;
    }
  }

  // Track child statuses separately — never let master_status overwrite child Hold
  if (!isParentRequisition && r.child_job_id) {
    grouped[key].child_statuses.push({
      id: r.id,
      child_job_id: r.child_job_id,
      status: r.status,
    });
  }

    if (Array.isArray(r.recruiter_details)) {
      grouped[key].recruiter_details.push(...r.recruiter_details);
    }

    if (Array.isArray(r.recruiter_details)) {
          r.recruiter_details.forEach((rec) => {
            if (rec?.id) {
                grouped[key].recruiter_details.push({
                id: rec.id,
                name: rec.name || "Unknown",
                recruiter_target: rec.recruiter_target || 0,
                           });
                     }
             });
       }
    if (r.id) {
      grouped[key].child_ids.push(r.id);
    }

    if (r.child_job_id) {
      grouped[key].child_job_ids.push(r.child_job_id);
    }

    const recordOpenings = Number(r.openings ?? r.master_openings ?? r.target ?? 1);
    grouped[key].openings = Math.max(Number(grouped[key].openings) || 0, recordOpenings);

    grouped[key].submissions_count =
      (grouped[key].submissions_count || 0) +
      (Number(r.submissions_count) || 0);

    grouped[key].joined_count =
      (grouped[key].joined_count || 0) +
      (Number(r.joined_count) || 0);
  });

  const merged = Object.values(grouped).map((item) => {
    const sortedChildJobIds = Array.isArray(item.child_job_ids)
      ? [...item.child_job_ids].sort((a, b) => {
          const aNum = parseInt(String(a).split("-").pop(), 10) || 0;
          const bNum = parseInt(String(b).split("-").pop(), 10) || 0;
          return aNum - bNum;
        })
      : item.child_job_ids;

    const sortedChildIds =
      Array.isArray(item.child_ids) && item.child_ids.every((id) => !isNaN(Number(id)))
        ? [...item.child_ids].sort((a, b) => Number(a) - Number(b))
        : item.child_ids;
return {
  ...item,
  id: item.id ?? item.requisition_id,
  child_ids: sortedChildIds,
  child_job_ids: sortedChildJobIds,
  recruiter_details: item.recruiter_details.filter(
    (rec, index, self) =>
      index === self.findIndex((r) => r.id === rec.id)
  ),
  recruiter_target: [...new Set(item.recruiter_target)],
 status: item.master_status || item.status,
  child_statuses: item.child_statuses || [],
};
  });

  setRequisitions(merged);


      } else {
        console.warn("No requisitions found or unexpected format");
        setRequisitions([]);
      }
    } catch (err) {
      console.error("Error fetching requisitions:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to load requisitions",
        text: "Something went wrong. Please try again later.",
        confirmButtonColor: "#d33",
      });
    }
  }, [roleId, userId]);

  useEffect(() => {
    fetchRequisitions();
    // Listen for assignment events to refresh counts
    const handler = () => fetchRequisitions();
    window.addEventListener('requisitionsUpdated', handler);
    return () => window.removeEventListener('requisitionsUpdated', handler);
  }, [fetchRequisitions]);

  // Fetch recruiters for role_id 3 (Hiring Manager)
  useEffect(() => {
    if (roleId === "3") {
      const fetchRecruiters = async () => {
        try {
          const res = await getAllRecruiters();
          if (res?.status === "S" && Array.isArray(res.result_info)) {
            setRecruiterOptions(
              res.result_info.map((u) => ({
                value: u.id,
                label: u.name,
                id: u.id,
                email: u.email,
              }))
            );
          } else {
            setRecruiterOptions([]);
          }
        } catch (error) {
          console.error("Error fetching recruiters:", error);
          setRecruiterOptions([]);
        }
      };
      fetchRecruiters();
    }
  }, [roleId]);

  // Ensure selected recruiters are always available in recruiterOptions
  useEffect(() => {
    if (selectedRecruiters.length > 0) {
      const existingIds = new Set(recruiterOptions.map(opt => Number(opt.value)));
      const missingRecruiters = selectedRecruiters.filter(
        sel => !existingIds.has(Number(sel.value))
      );
      
      if (missingRecruiters.length > 0) {
        setRecruiterOptions(prev => [
          ...prev,
          ...missingRecruiters.map(rec => ({
            value: Number(rec.value),
            label: rec.label,
            id: Number(rec.value),
            email: rec.email || "",
          }))
        ]);
      }
    }
  }, [selectedRecruiters, recruiterOptions]);

  const fetchJoinedCandidatesCount = async (requisition) => {
    if (!requisition) {
      setJoinedCandidatesCount(0);
      return;
    }

    const childIds = Array.isArray(requisition.child_ids) && requisition.child_ids.length > 0
      ? requisition.child_ids
      : [requisition.id];

    try {
      const responses = await Promise.all(
        childIds.map((childId) =>
          getCandidatesByRequisition({ requisition_id: Number(childId) })
        )
      );

      const joined = responses.reduce((sum, res) => {
        if (res?.result_code === 200 && Array.isArray(res.result_info)) {
          return (
            sum +
            res.result_info.filter((item) =>
              item.status && item.status.toLowerCase().includes("joined")
            ).length
          );
        }
        return sum;
      }, 0);

      setJoinedCandidatesCount(joined);
    } catch (error) {
      console.error("Error fetching assigned candidates:", error);
      setJoinedCandidatesCount(0);
    }
  };

  const validateAndSetRecruiterTarget = (rec, rawValue) => {
    // DEBUG: log entry
    console.debug('[RecruiterTarget] validate called', { recValue: rec?.value, rawValue, suppress: suppressValidationRef.current });

    if (suppressValidationRef.current) {
      console.debug('[RecruiterTarget] validation suppressed for', rec?.value);
      suppressValidationRef.current = false;
      return false;
    }

    const raw = String(rawValue || "").trim();
    const openings = Number(selectedRequisition?.openings || 0);
    const isExisting = (originalSelectedRecruiters || []).some(
      (orig) => String(orig.value) === String(rec.value)
    );

    if (raw === "") {
      setRecruiterTargets((prev) => ({
        ...prev,
        [rec.value]: "",
      }));
      return false;
    }

    if (!/^\d+$/.test(raw)) {
      return false;
    }

    const newVal = Number(raw);
    if (!isExisting && newVal <= 0) {
      return false;
    }

    if (newVal < 0) {
      return false;
    }

    // Compute otherSum from current state to handle quick input changes (arrow/spinner)
    const otherSum = Object.keys(recruiterTargets || {}).reduce((sum, key) => {
      if (String(key) === String(rec.value)) return sum;
      return sum + Number(recruiterTargets[key] || 0);
    }, 0);

    const proposedTotal = otherSum + newVal;

    console.debug('[RecruiterTarget] computed', { otherSum, newVal, proposedTotal, openings, selectedCount: selectedRecruiters.length });

    if (newVal > openings || proposedTotal > openings) {
      console.debug('[RecruiterTarget] triggering alert', { rec: rec?.value, newVal, openings, proposedTotal });
      if (!suppressValidationRef.current) {
        showAlert({
          icon: "warning",
          title: "Exceeded openings",
          text: `Allocation cannot exceed the number of openings for this requisition.`,
          allowOutsideClick: false,
        });
      }
      return true;
    }

    // Commit the valid value
    setRecruiterTargets((prev) => ({
      ...prev,
      [rec.value]: newVal,
    }));
    return false;
  };

  // Centralized alert helper that respects suppression flag
  const showAlert = (options) => {
    if (suppressValidationRef.current) {
      // Keep suppression for a short while to avoid spurious popups
      return;
    }
    return Swal.fire(options);
  };

  // Quick check utility used by input handlers to ensure immediate validation
  const checkExceeded = (rec, rawValue) => {
    if (suppressValidationRef.current) return false;
    const raw = String(rawValue || '').trim();
    if (!/^\d+$/.test(raw)) return false;
    const newVal = Number(raw);
    const openings = Number(selectedRequisition?.openings || 0);
    const otherSum = Object.keys(recruiterTargets || {}).reduce((sum, key) => {
      if (String(key) === String(rec.value)) return sum;
      return sum + Number(recruiterTargets[key] || 0);
    }, 0);
    const proposedTotal = otherSum + newVal;
    if (newVal > openings || proposedTotal > openings) {
      showAlert({
        icon: 'warning',
        title: 'Exceeded openings',
        text: `Allocation cannot exceed the number of openings for this requisition.`,
        allowOutsideClick: false,
      });
      return true;
    }
    return false;
  };

  const changeRecruiterTarget = (rec, delta) => {
    const currentVal = Number(recruiterTargets[rec.value] || 0);
    const isExisting = (originalSelectedRecruiters || []).some(
      (orig) => String(orig.value) === String(rec.value)
    );
    const minValue = isExisting ? 0 : 1;
    const nextVal = Math.max(currentVal + delta, minValue);
    if (delta < 0 && currentVal <= minValue) return;
    const rawValue = String(nextVal);
    if (checkExceeded(rec, rawValue)) return;
    validateAndSetRecruiterTarget(rec, rawValue);
  };

  const handleStatusChange = async (id, newStatus) => {
  try {
    const currentReq = requisitions.find((req) => req.id === id);

    // Prevent changes if already Closed
    if (currentReq?.status === "Closed") {
      Swal.fire({
        icon: "warning",
        title: "Closed requisitions cannot be modified",
      });
      return;
    }

    // Confirmation popup before closing requisition
    if (newStatus === "Closed") {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Are you sure you want to close this requisition? Once closed, no further changes can be made.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Close Requisition",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });

      // If user clicks Cancel
      if (!result.isConfirmed) {
        return;
      }
    }

    const payload = {
      status: newStatus,
    };

    const res = await updateRequisitionStatus(id, payload);
if (res?.result_code === 200 || res?.status === "S") {
  await fetchRequisitions();
  
  Swal.fire({
    icon: "success",
    title: "Status updated successfully",
    timer: 1200,
    showConfirmButton: false,
  });

   } else {
      Swal.fire({
        icon: "error",
        title: "Failed to update status",
      });
    }
  } catch (error) {
    console.error(error);

    Swal.fire({
      icon: "error",
      title: "Something went wrong",
    });
  }
};
const handleAssignRecruiter = async () => {
   const status = requisitions.find((r) => r.id === selectedRequisition.id)?.status;
  if(status === "Closed" || status === "Hold") {
    Swal.fire({
      icon: "warning",
      title: "Action not allowed",
      text: "Cannot assign recruiters to a requisition that is Closed or On Hold.",
    });
    return;
  }
  const allowEmptyRemoval = (originalSelectedRecruiters || []).length > 0;

  if (selectedRecruiters.length === 0 && !allowEmptyRemoval) {
    Swal.fire("Select at least one recruiter");
    return;
  }
 
  const invalid = selectedRecruiters.some((r) => {
    const value = recruiterTargets[r.value];
    const isExisting = (originalSelectedRecruiters || []).some(
      (orig) => String(orig.value) === String(r.value)
    );

    if (isExisting) {
      return value === undefined || value === "" || Number(value) < 0;
    }

    return value === undefined || value === "" || Number(value) <= 0;
  });

  const openings = Number(selectedRequisition?.openings || 0);
  const totalAssignedTargets = selectedRecruiters.reduce(
    (sum, r) => sum + Number(recruiterTargets[r.value] || 0),
    0
  );

  if (invalid) {
    Swal.fire("Please enter a valid target greater than 0 for all recruiters");
    return;
  }

  if (totalAssignedTargets > openings) {
    Swal.fire(
      "Exceeded openings",
      `Total recruiter targets cannot exceed ${openings} openings for this requisition`,
      "warning"
    );
    return;
  }
const payload = {
  requisition_id: Number(selectedRequisition.id),
  assigned_by: Number(userId || 1),

  recruiters: selectedRecruiters.map((r) => Number(r.value)),

  recruiter_targets: recruiterTargets
};
  try {
    // Check if any changes were made compared to original mapping
    const hasSelectedChanged = (() => {
      const origIds = (originalSelectedRecruiters || []).map((r) => String(r.value)).sort();
      const newIds = (selectedRecruiters || []).map((r) => String(r.value)).sort();
      if (origIds.length !== newIds.length) return true;
      for (let i = 0; i < origIds.length; i++) if (origIds[i] !== newIds[i]) return true;
      return false;
    })();

    const hasTargetsChanged = (() => {
      const origKeys = Object.keys(originalRecruiterTargets || {}).sort();
      const newKeys = Object.keys(recruiterTargets || {}).sort();
      if (origKeys.length !== newKeys.length) return true;
      for (let i = 0; i < origKeys.length; i++) if (origKeys[i] !== newKeys[i]) return true;
      for (const k of newKeys) {
        const a = String(originalRecruiterTargets[k] || "");
        const b = String(recruiterTargets[k] || "");
        if (a !== b) return true;
      }
      return false;
    })();

    if (!hasSelectedChanged && !hasTargetsChanged) {
      Swal.fire({ icon: 'info', title: 'No updates made', text: 'No changes detected in selected recruiters or targets.' });
      return;
    }

    // If there were existing mappings (originalSelectedRecruiters) treat as reconciliation
    let res;
    if ((originalSelectedRecruiters || []).length > 0 || isLocked) {
      res = await reconcileAllocations(payload);
    } else {
      res = await assignRecruiters(payload);
    }

    if (res?.result_code === 200 || res?.status === "S") {
      Swal.fire("Success", "Recruiters assigned successfully", "success");
      // Optimistically update local requisitions state for this requisition
      
       //  Close modal + dropdown
            setRecruiterModalOpen(false);
            setRecruiterMenuOpen(false);
         await fetchRequisitions();

      // Notify other views to refresh if needed
      try { window.dispatchEvent(new CustomEvent('requisitionsUpdated', { detail: { requisition_id: selectedRequisition.id } })); } catch (e) {}

      // Keep modal open, just refresh the data from backend
      try {
        const mappingsResponse = await getRecruiterMappings(selectedRequisition.id);
        if (mappingsResponse?.result_code === 200) {
          const { recruiter_targets, recruiter_details } = mappingsResponse.result_info;
          const fetchedRecruitersAsOptions = recruiter_details.map(rec => ({
            value: Number(rec.id),
            label: rec.name,
            id: Number(rec.id),
            email: rec.email || "",
          }));
          const selectedRecruitersFromMapping = fetchedRecruitersAsOptions.map(rec => ({
            value: Number(rec.value),
            label: rec.label,
          }));
          setSelectedRecruiters(selectedRecruitersFromMapping);
          setRecruiterTargets(recruiter_targets);
          setOriginalSelectedRecruiters(selectedRecruitersFromMapping);
          setOriginalRecruiterTargets(recruiter_targets || {});
        }
      } catch (e) {
        console.error("Error refreshing recruiter mappings:", e);
      }
    } else {
      Swal.fire("Error", "Failed to assign recruiters", "error");
    }
  } catch (error) {
    console.error(error);
    Swal.fire("Error", "Something went wrong", "error");
  }
};
  // Open assign recruiter modal
  const openAssignRecruiterModal = async (requisition) => {
    const status = requisitions.find((r) => r.id === requisition.id)?.status;
    const isAlreadyAssigned = requisition.recruiter_details && requisition.recruiter_details.length > 0;
    setLocked(isAlreadyAssigned);
    if(status === "Open" || status === "Hold" || status === "Closed") {
      setSelectedRequisition(requisition);

      try {
        // Fetch existing recruiter mappings
        const mappingsResponse = await getRecruiterMappings(requisition.id);
        
        if (mappingsResponse?.result_code === 200) {
          const { recruiter_targets, recruiter_details } = mappingsResponse.result_info;
          
          // FIRST: Ensure all fetched recruiters are in the options list before setting selectedRecruiters
          const fetchedRecruitersAsOptions = recruiter_details.map(rec => ({
            value: Number(rec.id),
            label: rec.name,
            id: Number(rec.id),
            email: rec.email || "",
          }));
          
          // Merge fetched recruiters with existing options, avoiding duplicates
          const existingIds = new Set(recruiterOptions.map(opt => Number(opt.value)));
          const uniqueFetchedRecruiters = fetchedRecruitersAsOptions.filter(
            rec => !existingIds.has(Number(rec.value))
          );
          
          // Update recruiterOptions with all fetched recruiters first
          if (uniqueFetchedRecruiters.length > 0) {
            setRecruiterOptions(prev => {
              const currentIds = new Set(prev.map(p => Number(p.value)));
              const toAdd = uniqueFetchedRecruiters.filter(r => !currentIds.has(Number(r.value)));
              return [...prev, ...toAdd];
            });
          }
          
          // THEN: Set selected recruiters with proper structure
          const selectedRecruitersFromMapping = fetchedRecruitersAsOptions.map(rec => ({
            value: Number(rec.value),
            label: rec.label,
          }));
          
          setSelectedRecruiters(selectedRecruitersFromMapping);
          setRecruiterTargets(recruiter_targets);
          // Save originals for change detection
          setOriginalSelectedRecruiters(selectedRecruitersFromMapping);
          setOriginalRecruiterTargets(recruiter_targets || {});
        } else {
          // No mappings found, reset states
          setSelectedRecruiters([]);
          setRecruiterTargets({});
          setOriginalSelectedRecruiters([]);
          setOriginalRecruiterTargets({});
        }
      } catch (error) {
        console.error("Error fetching recruiter mappings:", error);
        setSelectedRecruiters([]);
        setRecruiterTargets({});
      }
      
      fetchJoinedCandidatesCount(requisition);
      setRecruiterModalOpen(true);
    }
  };

  // Helper to format date as dd-mm-yyyy
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d)) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Filter requisitions based on search input
  // Search in Job ID, Job Title, Department, and Recruiter
  const filteredRequisitions = requisitions.filter((r) => {
    const searchTerm = search.toLowerCase();
    
    // Search in Job ID
    const jobIdMatch = (r.job_id || r.id || "").toString().toLowerCase().includes(searchTerm);
    
    // Search in Job Title
    const jobTitleMatch = (r.job_title || "").toLowerCase().includes(searchTerm);
    
    // Search in Department
    const departmentMatch = r.department_details && 
      r.department_details.some((dept) =>
        (dept.name || "").toLowerCase().includes(searchTerm)
      );
    
        // Search in Recruiter
    const recruiterMatch = r.recruiter_details && 
      r.recruiter_details.some((rec) =>
        (rec.name || "").toLowerCase().includes(searchTerm)
      );

    // Search in Created Date
    const dateMatch = r.created_at && 
      formatDate(r.created_at).toLowerCase().includes(searchTerm);

    if (roleId === "4") {
      // For recruiters, search in Job ID, Job Title, and Created Date
      return jobIdMatch || jobTitleMatch || dateMatch;
    } else {
      // For other roles, search in all fields: Job ID, Job Title, Department, Recruiter, and Created Date
      return jobIdMatch || jobTitleMatch || departmentMatch || recruiterMatch || dateMatch; 
    }
  });

  const pageCount = Math.ceil(filteredRequisitions.length / ITEMS_PER_PAGE);
  const paginatedRequisitions = filteredRequisitions.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const totalAssignedTargets = selectedRecruiters.reduce(
    (sum, r) => sum + Number(recruiterTargets[r.value] || 0),
    0
  );

  

  const getNamesFromDetails = (details) => {
    return Array.isArray(details) && details.length > 0
      ? details.map((d) => d.name).join(", ")
      : "--";
  };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Requisitions Report");

    roleId === "4"
      ? // Add header row
        (worksheet.columns = [
          { header: "Parent Job ID", key: "parent_job_id" },
          { header: "Child Job ID", key: "child_job_id" },
          { header: "Job Title", key: "job_title" },
          { header: "Submissions", key: "submissions_count" },
          { header: "Status", key: "status" },
          { header: "Created On", key: "created_at" },
        ])
      : (worksheet.columns = [
          { header: "Parent Job ID", key: "parent_job_id" },
          // { header: "Child Job IDs", key: "child_job_ids" },
          { header: "Job Title", key: "job_title" },
          // { header: "Department", key: "department" },
          { header: "Recruiter", key: "recruiter" },
          { header: "Total Openings", key: "openings" },
          { header: "Status", key: "status" },
          { header: "Submissions", key: "submissions_count" },
          { header: "Created On", key: "created_at" },
        ]);
    // Add data rows
    requisitions.forEach((item, index) => {
      worksheet.addRow({
         parent_job_id: item.job_id || "--",
       // parent_job_id: item.parent_job_id || "--",
        child_job_id: item.child_job_id || "--",
        child_job_ids: Array.isArray(item.child_job_ids) ? item.child_job_ids.join(", ") : "--",
        job_title: item.job_title || "--",
        // department: getNamesFromDetails(item.department_details),
        recruiter: getNamesFromDetails(item.recruiter_details),
        openings: item.openings ?? 0,
        status: item.status || "--",
        submissions_count: item.submissions_count ?? 0,
        created_at: item.created_at ? formatDateForCSV(item.created_at) : "--",
      });
    });

    // Create a Blob from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Requisitions.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
  };

  // Reset to page 1 on search
  // React.useEffect(() => {
  //   if(!search){
  //     // setPage(1);
  //     localStorage.removeItem("searchTerm");
  //     localStorage.removeItem("page");
  //     setSearch("");
  //     setPage(1);
  //   }
  // }, [search]);

  

  // Helper to format date for CSV export (dd/mm/yyyy)
  const formatDateForCSV = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d)) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper to get display text for arrays
  const getDisplayText = (details, fallback) => {
    if (details && Array.isArray(details) && details.length > 0) {
      return details.map((item) => item.name).join(", ");
    }
    return fallback || "-";
  };

  function getPageNumbers(currentPage, totalPages, maxPagesToShow = 5) {
    const pages = [];

    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(currentPage - half, 1);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxPagesToShow + 1, 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  const handleNavigate = () => {
    localStorage.setItem("searchTerm", search);
    localStorage.setItem("page", page.toString());
    // navigate("/requisitions/assignedlist");
  };

  useEffect(() => {
    const storedSearch = localStorage.getItem("searchTerm");
    const storedPage = localStorage.getItem("page");

    if (storedSearch) setSearch(storedSearch);
    if (storedPage) setPage(Number(storedPage));
  }, []);

  return (
    <div className="requisitions-page">
      <div className="requisitions-header-row">
        <div className="requisitions-title-group">
          <h1>{roleId === "4" ? "Assigned Requisitions" : "Requisitions"}</h1>
          {/* <p>
            {roleId === "4"
              ? "View and manage your assigned job requisitions"
              : "Manage and track all job requisitions"}
          </p> */}
        </div>
        <button className="export-btn" onClick={handleExport}>
          Export
        </button>
      </div>
      <div className="requisitions-table-controls">
        <input
          className="custom-placeholder"
          placeholder={
            roleId === "4"
              ? "Search by Job ID, Job Title, or Date"
              : "Search by Job ID, Job Title, Department, Recruiter, or Date"
          }
          value={search}
          style={{
            minWidth: 500,
            maxWidth: 300,
            padding: "0.4rem .5rem",
            border: "1.5px solid #d1d5db",
            borderRadius: 6,
            background: "#fff",
            fontSize: "1rem",
          }}
          
          onChange={(e) => {
            setSearch(e.target.value)
            if(!e.target.value){
              // setPage(1);
              localStorage.removeItem("searchTerm");
              localStorage.removeItem("page");
              setSearch("");
              setPage(1);
            }
          }}
        />
        {/* Hide Create Requisition button for role_id 4 */}
        {roleId === "3" && (
          <Link to="/requisitions/create" className="create-btn">
            Create Requisition
          </Link>
        )}
      </div>
      <div className="requisitions-table-wrapper">
        <table className="requisitions-table">
          <thead>
            <tr>
              <th>Parent Job ID</th>
              {roleId === "4" && <th>Child Job ID</th>}
              <th>Job Title</th>
              {roleId !== "4" && <th>Total Openings</th>}
             {roleId !== "4" && <th>Current Openings</th>}
              {/* {roleId === "4" && <th>Targets</th>} */}
              {/* {roleId !== "4" && <th>Department</th>} */}
              {roleId !== "4" && <th>Recruiters</th>}
              {roleId === "4" && <th>Targets</th>}
              <th>Submissions</th>
              <th>Status</th>
              <th>Created On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRequisitions.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  style={{ textAlign: "center" }}
                >
                  {roleId === "4"
                    ? "No assigned requisitions found"
                    : "No requisitions found"}
                </td>
              </tr>
            ) : (
              paginatedRequisitions.map((requisition) => (
                <tr key={requisition.id}>
                 {roleId !== "4" && <td>{requisition.job_id || "-"}</td>}
                  {roleId === "4" && <td>{requisition.parent_job_id || "-"}</td>}
                  {roleId === "4" && <td>{requisition.child_job_id || "-"}</td>}
                  <td>{requisition.job_title}</td>
                 {roleId !== "4" && <td>{Number(requisition.openings ?? requisition.master_openings ?? requisition.target ?? 0)}</td>}
                 {roleId !== "4" && <td>{Math.max((Number(requisition.openings ?? requisition.master_openings ?? requisition.target ?? 0) || 0) - (Number(requisition.joined_count) || 0), 0)}</td>}
                  {/* {roleId === "4" && (
                    <td>
                      {Array.isArray(requisition.recruiter_target) && requisition.recruiter_target.length > 0
                        ? requisition.recruiter_target.join(", ")
                        : requisition.recruiter_target 
                          ? requisition.recruiter_target
                          : "-"
                      }
                    </td>
                  )} */}
                  {/* {roleId !== "4" && (
                    <td>
                      {getDisplayText(
                        requisition.department_details,
                        requisition.departments
                      )}
                    </td>
                  )} */}
                  {roleId !== "4" && (
                    <td>
                      {Array.isArray(requisition.recruiter_details) && requisition.recruiter_details.length > 0
                        ? requisition.recruiter_details.map((r) => r?.name || "--").join(", ")
                        : "--"}
                    </td>
                  )}
                  {roleId === "4" && (
                    <td>
                      {requisition.recruiter_target ?? "--"}
                    </td>
                  )}
                  {/* {roleId !== "4" && <td>{requisition.openings}</td>} */}

                  <td>
                    {typeof requisition.submissions_count !== 'undefined'
                      ? requisition.submissions_count
                      : "-"}
                  </td>

                  <td>
                    {roleId === "3" ? (
                      <select
                        className={`status-badge ${
                          requisition.status
                            ? requisition.status.toLowerCase().replace(/\s+/g, "-")
                            : ""
                        }`}
                        value={requisition.status || ""}
                        onChange={(e) =>
                          handleStatusChange(requisition.id, e.target.value)
                        }
                        disabled={requisition.status === "Closed"}
                      >
                        <option value="Open">Open</option>
                        <option value="Hold">Hold</option>
                        <option value="Closed">Closed</option>
                      </select>
                    ) : (
                      <span
                        className={`status-badge ${requisition.status ? requisition.status.toLowerCase().replace(/\s+/g, "-") : ""}`}
                      >
                        {requisition.status || "-"}
                      </span>
                    )}
                  </td>
                                <td>
                    {requisition.created_at
                      ? formatDate(requisition.created_at)
                      : "-"}
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {/* Role-based action buttons */}
                      {roleId === "1" && (
                        <>
                          {/* View button for role_id 1 */}
                          <button
                            className="view-btn"
                            style={{
                              background: "transparent",
                              color: "#007bff",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() => { handleNavigate(); navigate(`/requisitions/edit/${requisition.id}`); }}
                            title="View"
                          >
                            <FaEye />
                          </button>
                          <button
                            className="view-btn"
                            style={{
                              background: "transparent",
                              color: "#007bff",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() => {
                              handleNavigate();
                              const numericId = Number.isFinite(Number(requisition.id)) ? Number(requisition.id) : undefined;
                              const jobIdForDisplay = requisition.job_id || requisition.child_job_id;
                              console.log("Navigating to AssignedList with numeric requisition_id:", numericId, "jobId:", jobIdForDisplay);

                              navigate("/requisitions/assignedlist", {
                                state: {
                                  requisition_ids: requisition.child_ids || [numericId],
                                  requisition_id: numericId,
                                  jobId: jobIdForDisplay,
                                  requisition: {
                                    correl_id: requisition.correl_id,
                                    id: numericId,
                                    jobTitle: requisition.job_title,
                                    location: requisition.locations || "-",
                                    department: getDisplayText(
                                      requisition.department_details,
                                      requisition.departments
                                    ),
                                    hiringManager: getDisplayText(
                                      requisition.recruiter_details,
                                      requisition.recruiters
                                    ),
                                    openings: requisition.openings,
                                    current_openings: Math.max((Number(requisition.openings) || 0) - (Number(requisition.joined_count) || 0), 0),
                                    description: requisition.job_desc,
                                    skills: requisition.skills
                                      ?.split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  },
                                },
                              });
                            }}

                            title="Assigned List"
                          >
                            <FaList />
                          </button>
                        </>
                      )}

                      {roleId === "4" && (
                        <>
                          {/* View button for role_id 4 */}
                          <button
                            className="view-btn"
                            style={{
                              background: "transparent",
                              color: "#007bff",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() => { handleNavigate(); navigate(`/requisitions/edit/${requisition.requisition_id || requisition.id}`); }}
                            title="View Requisition"
                          >
                            <FaEye />
                          </button>
                          {/* Assign Candidate button for role_id 4 */}
                          <button
                            className="assign-btn"
                            style={{
                              background: "transparent",
                              color: "#28a745",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() => {
                              const status = (requisition?.status || '').toString().toLowerCase();
                              if (status.includes('closed') || status.includes('hold')) {
                                Swal.fire({
                                  icon: 'warning',
                                  title: 'Action not allowed',
                                  text: `Cannot assign candidates to a requisition that is ${requisition?.status || 'not Open'}.`,
                                  confirmButtonColor: '#3085d6',
                                });
                                return;
                              }
                              handleNavigate();
                              console.log('Requisition Object:', requisition);
                              navigate('/requisitions/assign', {
                                state: {
                                  [requisition.requisition_id || requisition.id]: requisition.requisition_id || requisition.id,
                                  requisition_id: requisition.requisition_id || requisition.id,

                                  requisition: {
                                    correl_id: requisition.correl_id,
                                    id: requisition.requisition_id || requisition.id,
                                    jobTitle: requisition.job_title,

                                    location: requisition.locations || "-",
                                    // departments: getDisplayText(
                                    //   requisition.department_details,
                                    //   requisition.departments
                                    // ),
                                    department: getDisplayText(
                                      requisition.department_details,
                                      requisition.departments
                                    ),
                                    hiringManager: getDisplayText(
                                      requisition.recruiter_details,
                                      requisition.recruiters
                                    ),
                                    openings: requisition.openings,
                                    description: requisition.job_desc,
                                    skills: requisition.skills
                                      ?.split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  },
                                },
                              })
                            }}
                            title="Assign Candidates"
                          >
                            <FaUserPlus />
                          </button>
                          <button
                            className="view-btn"
                            style={{
                              background: "transparent",
                              color: "#007bff",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() => {
                              handleNavigate();
                              const actualId = requisition.requisition_id || requisition.id;
                              const jobIdForDisplay = requisition.child_job_id || requisition.parent_job_id || requisition.job_id;
                              navigate("/requisitions/assignedlist", {
                                state: {
                                  requisition_id: actualId,
                                  jobId: jobIdForDisplay,
                                  requisition: {
                                    correl_id: requisition.correl_id,
                                    id: actualId,
                                    jobTitle: requisition.job_title,
                                    // location: getDisplayText(
                                    //   requisition.location_details,
                                    //   requisition.location
                                    // ),
                                    // department: getDisplayText(
                                    //   requisition.department_details,
                                    //   requisition.departments
                                    // ),
                                    location: requisition.locations || "-",
                                    department: getDisplayText(
                                      requisition.department_details,
                                      requisition.departments
                                    ),
                                    hiringManager: getDisplayText(
                                      requisition.recruiter_details,
                                      requisition.recruiters
                                    ),
                                    openings: requisition.openings,
                                    description: requisition.job_desc,
                                    skills: requisition.skills
                                      ?.split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  },
                                },
                              });
                            }}
                            title="Assigned List"
                          >
                            <FaList />
                          </button>
                        </>
                      )}

                      {roleId === "3" && (
                        <>
                          {/* Edit button for role_id 3 */}
                          <button
                            className="edit-btn"
                            onClick={() => { handleNavigate(); navigate(`/requisitions/edit/${requisition.id}`); }}
                            title="View"
                          >
                            <FaEye />
                          </button>
                          {/* Assign Recruiters button for role_id 3 */}
                         {roleId === "3" && (() => {
                          const isDisabled = requisition.status === "Closed" || requisition.status === "Hold";
                           return (
                                 <button
                                 className="update-btn"
                                 disabled={isDisabled}
                                 onClick={() => {
                                 if (isDisabled) return;
                                 handleNavigate();
                                 navigate(`/requisitions/update/${requisition.id}`);
                                       }}
                                 title="Update Requisition"
                                 style={{
                                 background: "transparent",
                                 border: "none",
                                 padding: "0.4rem 0.8rem",
                                 borderRadius: "4px",
                                 fontSize: "0.9rem",
                                 fontWeight: "500",
                                 cursor: isDisabled ? "not-allowed" : "pointer",
                                 opacity: isDisabled ? 0.4 : 1,
                                 pointerEvents: isDisabled ? "none" : "auto",
                                     }}
                                        >
                                <FaEdit size={18} color={isDisabled ? "#aaa" : "#0078B9"} />
                        </button>
                                );
                         })()}
                          <button
                            className="assign-btn"
                            style={{
                              background: "transparent",
                              color: "#28a745",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() =>
                              openAssignRecruiterModal(requisition)
                            }
                            title="Assign Recruiter"
                          >
                            <FaUserPlus />
                          </button>
                          <button
                            className="view-btn"
                            style={{
                              background: "transparent",
                              color: "#007bff",
                              border: "none",
                              padding: "0.4rem 0.8rem",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                            }}
                            onClick={() => {
                              handleNavigate();
                              const numericId = Number.isFinite(Number(requisition.id)) ? Number(requisition.id) : undefined;
                              const jobIdForDisplay = requisition.job_id || requisition.child_job_id;
                              navigate("/requisitions/assignedlist", {
                                state: {
                                  requisition_ids: requisition.child_ids || [numericId],
                                  jobId: jobIdForDisplay,
                                  requisition: {
                                    correl_id: requisition.correl_id,
                                    id: numericId,
                                    jobTitle: requisition.job_title,
                                    location: requisition.locations || "-",
                                    department: getDisplayText(
                                      requisition.department_details,
                                      requisition.departments
                                    ),

                                    hiringManager: getDisplayText(
                                      requisition.recruiter_details,
                                      requisition.recruiters
                                    ),
                                    openings: requisition.openings,
                                    current_openings: Math.max((Number(requisition.openings) || 0) - (Number(requisition.joined_count) || 0), 0),
                                    description: requisition.job_desc,
                                    skills: requisition.skills
                                      ?.split(",")
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  },
                                },
                              });
                            }}
                            title="Assigned List"
                          >
                            <FaList />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
       {pageCount > 1 && (
  <div className="settings-pagination">
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page - 1)}
      disabled={page === 1}
    >
      &lt;
    </button>
    {getPageNumbers(page, pageCount, 5).map((pageNum) => (
      <button
        key={pageNum}
        className={"pagination-btn" + (page === pageNum ? " active" : "")}
        onClick={() => handlePageChange(pageNum)}
      >
        {pageNum}
      </button>
    ))}
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page + 1)}
      disabled={page === pageCount}
    >
      &gt;
    </button>
  </div>
)}
      {recruiterModalOpen && roleId === "3" && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.25)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="modal-content"
            style={{
              background: "#fff",
              borderRadius: 10,
              padding: 32,
              minWidth: 500,
              width: "400px",
              boxShadow: "0 2px 16px rgba(0,0,0,0.15)",
            }}
          >
            <h5 style={{ marginBottom: 18, fontWeight: 600 }}>
              Assign Recruiter
            </h5>
            <div style={{ marginBottom: 16 }}>
              <label
                style={{ fontWeight: 500, marginBottom: 6, display: "block" }}
              >
                Select Recruiter *
              </label>
              <Select             
                isMulti
                isDisabled={isLocked}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ Option: CheckboxOption }}
                menuIsOpen={recruiterMenuOpen}
                onMenuOpen={() => setRecruiterMenuOpen(true)}
                onMenuClose={() => setRecruiterMenuOpen(false)}
                styles={{
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#fff" : state.isFocused ? "#f4f5f7" : "#fff",
                    color: "#111",
                  }),
                  multiValue: (provided) => ({
                    ...provided,
                    backgroundColor: "#e2e8f0",
                  }),
                  multiValueLabel: (provided) => ({
                    ...provided,
                    color: "#111",
                  }),
                  multiValueRemove: (provided) => ({
                    ...provided,
                    color: "#6b7280",
                    ':hover': {
                      backgroundColor: '#cbd5e1',
                      color: '#111',
                    },
                  }),
                }}
                options={recruiterOptions}
                controlShouldRenderValue={false}
                value={selectedRecruiters}
                onChange={(selected) => {
                  const list = selected || [];
                  const selectAllClicked = list.some((opt) => opt.value === "__select_all__");
                  const regularSelected = list.filter((opt) => opt.value !== "__select_all__");
                  const openings = Number(selectedRequisition?.openings || 0);

                  if (selectAllClicked) {
                    const allToSelect = openings > 0 ? recruiterOptions.slice(0, openings) : recruiterOptions;
                    setSelectedRecruiters(allToSelect);
                    setRecruiterTargets((prev) => {
                      const updated = {};
                      allToSelect.forEach((rec) => {
                        const key = String(rec.value);
                        updated[key] = prev[key] !== undefined ? prev[key] : "";
                      });
                      return updated;
                    });
                    return;
                  }

                  // Check if openings is zero
                  if (openings === 0 && regularSelected.length > 0) {
                    showAlert({
                      icon: "warning",
                      title: "No openings available",
                      text: "Cannot assign recruiters when there are no openings for this requisition.",
                      allowOutsideClick: false,
                    });
                    setRecruiterMenuOpen(true);
                    return;
                  }

                  if (openings > 0 && regularSelected.length > openings) {
                    showAlert({
                      icon: "warning",
                      title: "Selection limit",
                      text: `Recruiter count cannot exceed the available openings.`,
                      allowOutsideClick: false,
                    });
                    setRecruiterMenuOpen(true);
                    return;
                  }

                  setSelectedRecruiters(regularSelected);
                  setRecruiterTargets((prev) => {
                    const updated = {};
                    regularSelected.forEach((rec) => {
                      const key = String(rec.value);
                      updated[key] = prev[key] !== undefined ? prev[key] : "";
                    });
                    return updated;
                  });
                }}
                getOptionValue={(option) => String(option.value)}
                getOptionLabel={(option) => option.label}
                formatOptionLabel={(option) => `${option.label}`}
                placeholder="Select recruiters..."
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1rem",
                  marginTop: 16,
                }}
              >
              </div>
              {selectedRecruiters.length > 0 && (
  <div style={{ marginTop: "15px", maxHeight: "260px", overflowY: "auto", paddingRight: "4px" }}>
    <h6 style={{ marginBottom: "10px", fontWeight: 600 }}>Selected Recruiters</h6>

    {selectedRecruiters.map((rec) => (
      <div
        key={rec.value}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 12px",
          border: "1px solid #ddd",
          borderRadius: "6px",
          marginBottom: "8px",
          background: "#f9f9f9",
          gap: "10px"
        }}
      >
        {/* Recruiter Name */}
        <div style={{ flex: 1, fontWeight: 500 }}>
          {rec.label}
        </div>

        {/* Target Label */}
        <label style={{ minWidth: "70px", fontWeight: 500, marginBottom: 0 }}>
          Target:
        </label>

        {/* Target Input */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <button
            type="button"
            disabled={Number(recruiterTargets[rec.value] || 0) <= 1 || isLocked}
            style={{
              width: "20px",
              height: "34px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "#f4f5f7",
              cursor: Number(recruiterTargets[rec.value] || 0) <= 1 || isLocked ? "not-allowed" : "pointer",
              color: "#111",
              fontWeight: 700,
            }}
            onClick={() => changeRecruiterTarget(rec, -1)}
          >
            -
          </button>

          <input
            type="text"
            disabled = {isLocked}
            inputMode="numeric"
            pattern="[0-9]*"
            value={recruiterTargets[rec.value] ?? ""}
            style={{
              width: "50px",
              padding: "6px 8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
              cursor: isLocked ? "not-allowed" : "text",
              textAlign: "center",
            }}
            onKeyDown={(e) => {
              if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              if (checkExceeded(rec, e.currentTarget.value)) return;
              validateAndSetRecruiterTarget(rec, e.currentTarget.value);
            }}
            onChange={(e) => {
              if (checkExceeded(rec, e.target.value)) return;
              validateAndSetRecruiterTarget(rec, e.target.value);
            }}
          />

          <button
            type="button"
            disabled={isLocked}
            style={{
              width: "20px",
              height: "34px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "#f4f5f7",
              cursor: isLocked ? "not-allowed" : "pointer",
              color: "#111",
              fontWeight: 700,
            }}
            onClick={() => changeRecruiterTarget(rec, 1)}
          >
            +
          </button>
        </div>

        {/* Remove Button */}
        <button
        disabled={isLocked}
          style={{
            marginLeft: "10px",
            background: "#ff4444",
            border: "none",
            color: "white",
            cursor: isLocked ? "not-allowed" : "pointer",
            fontWeight: "bold",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "14px",
          }}
          onClick={() => {
            const updated = selectedRecruiters.filter(
              (r) => r.value !== rec.value
            );
            setSelectedRecruiters(updated);

            const newTargets = { ...recruiterTargets };
            delete newTargets[rec.value];
            setRecruiterTargets(newTargets);
          }}
        >
          Remove
        </button>
      </div>
    ))}
  </div>
)}
  <div style={{ marginTop: "15px", display: "flex", justifyContent: "space-between", padding: "10px 12px", border: "1px solid #eee", borderRadius: "6px", }}>
           <div>
                <strong>Allocated</strong>{" "}
                {totalAssignedTargets}
              </div>
              <div>
                <strong>Remaining</strong>{" "}
                {selectedRequisition ? Math.max((Number(selectedRequisition.openings) || 0) - totalAssignedTargets, 0) : 0}
                
              </div>
              </div>
           
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 20,
                gap: 12,
              }}
            >
              <button
                style={{
                  padding: "0.5rem 1.2rem",
                  background: "#e5e7eb",
                  border: "none",
                  borderRadius: 6,
                  fontWeight: 500,
                }}
                onMouseDown={() => {
                  suppressValidationRef.current = true;
                  // Clear suppression shortly after the click so subsequent edits still validate
                  setTimeout(() => {
                    suppressValidationRef.current = false;
                  }, 300);
                }}
                onClick={() => {
                  setRecruiterModalOpen(false);
                  setRecruiterMenuOpen(false);
                  setSelectedRecruiters([]);
                  setRecruiterTargets({});
                  setSelectedRequisition(null);
                }}
              >
                Cancel
              </button>
              <button
              disabled={isLocked}
                style={{
                  padding: "0.5rem 1.2rem",
                  background: "#0078b9",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: isLocked ? "not-allowed" : "pointer",
                  fontWeight: 500,
                }}
                onClick={handleAssignRecruiter}
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Requisitions;
